
import { 
  saveCampaign as firebaseSaveCampaign,
  updateCampaignData as firebaseUpdateCampaignData,
  getCampaignData as firebaseGetCampaignData,
  getUserCampaigns as firebaseGetUserCampaigns,
  deleteCampaign as firebaseDeleteCampaign
} from './firebase';
import { findMatchingCampaign } from './campaignMatcher';

// Simple wrapper that tries Firebase first, with localStorage fallback only on specific errors
const STORAGE_PREFIX = 'campaign_local_';

const isConnectionError = (error: any): boolean => {
  const message = error?.message?.toLowerCase() || '';
  return message.includes('network') || 
         message.includes('offline') || 
         message.includes('connection') ||
         message.includes('fetch');
};

export const findOrCreateCampaign = async (userId: string, campaignData: any): Promise<string> => {
  try {
    // First, try to find existing campaigns
    const existingCampaigns = await getUserCampaignsSimple(userId);
    const match = findMatchingCampaign(campaignData, existingCampaigns);
    
    if (match) {
      console.log('Found matching campaign:', match.id, 'with score:', match.score);
      return match.id;
    }
    
    // No match found, create new campaign
    console.log('No matching campaign found, creating new one');
    return await saveCampaignSimple(userId, campaignData);
  } catch (error) {
    console.error('Error in findOrCreateCampaign:', error);
    // Fallback to creating new campaign
    return await saveCampaignSimple(userId, campaignData);
  }
};

export const saveCampaignSimple = async (userId: string, campaignData: any): Promise<string> => {
  try {
    return await firebaseSaveCampaign(userId, campaignData);
  } catch (error) {
    console.error('Firebase save failed:', error);
    
    if (isConnectionError(error)) {
      // Only use localStorage for connection issues
      const id = crypto.randomUUID();
      const localData = {
        ...campaignData,
        userId,
        createdAt: new Date().toISOString(),
        localMode: true
      };
      
      localStorage.setItem(`${STORAGE_PREFIX}${id}`, JSON.stringify(localData));
      console.log('Saved to localStorage due to connection issue');
      return id;
    }
    
    throw error;
  }
};

export const updateCampaignDataSimple = async (campaignId: string, updates: any): Promise<void> => {
  try {
    await firebaseUpdateCampaignData(campaignId, updates);
  } catch (error) {
    console.error('Firebase update failed:', error);
    
    if (isConnectionError(error)) {
      // Try localStorage fallback
      const localKey = `${STORAGE_PREFIX}${campaignId}`;
      const existing = localStorage.getItem(localKey);
      
      if (existing) {
        const data = JSON.parse(existing);
        const updated = { ...data, ...updates };
        localStorage.setItem(localKey, JSON.stringify(updated));
        console.log('Updated in localStorage due to connection issue');
        return;
      }
    }
    
    throw error;
  }
};

export const getCampaignDataSimple = async (campaignId: string): Promise<any> => {
  try {
    return await firebaseGetCampaignData(campaignId);
  } catch (error) {
    console.error('Firebase get failed:', error);
    
    if (isConnectionError(error)) {
      // Try localStorage fallback
      const localKey = `${STORAGE_PREFIX}${campaignId}`;
      const data = localStorage.getItem(localKey);
      
      if (data) {
        console.log('Retrieved from localStorage due to connection issue');
        return JSON.parse(data);
      }
    }
    
    throw error;
  }
};

export const deleteCampaignSimple = async (campaignId: string): Promise<void> => {
  try {
    await firebaseDeleteCampaign(campaignId);
  } catch (error) {
    console.error('Firebase delete failed:', error);
    
    // Check for permission errors first
    if (error instanceof Error && error.message.includes('Missing or insufficient permissions')) {
      throw new Error('Permission denied: Unable to delete this campaign. Please check that you own this campaign and that Firebase security rules are properly configured.');
    }
    
    if (isConnectionError(error)) {
      // Try localStorage fallback
      const localKey = `${STORAGE_PREFIX}${campaignId}`;
      const data = localStorage.getItem(localKey);
      
      if (data) {
        localStorage.removeItem(localKey);
        console.log('Deleted from localStorage due to connection issue');
        return;
      }
    }
    
    throw error;
  }
};

export const getUserCampaignsSimple = async (userId: string): Promise<any[]> => {
  try {
    return await firebaseGetUserCampaigns(userId);
  } catch (error) {
    console.error('Firebase getUserCampaigns failed:', error);
    
    if (isConnectionError(error)) {
      // Scan localStorage for user's campaigns
      const campaigns: any[] = [];
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(STORAGE_PREFIX)) {
          try {
            const data = localStorage.getItem(key);
            if (data) {
              const campaign = JSON.parse(data);
              if (campaign.userId === userId) {
                campaigns.push({ id: key.replace(STORAGE_PREFIX, ''), ...campaign });
              }
            }
          } catch (parseError) {
            console.warn('Failed to parse campaign from localStorage:', key);
          }
        }
      }
      
      console.log('Retrieved campaigns from localStorage due to connection issue');
      return campaigns;
    }
    
    throw error;
  }
};
